'use client';

import React from 'react';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { devLog } from '@/lib/posthog/dev-log';
import { POSTHOG_CONFIG } from '@/lib/posthog/config';
/**
 * PostHog Provider Component
 * Uses official PostHog React provider for better Next.js App Router integration
 *
 * Features:
 * - Automatic initialization with optimized configuration
 * - Automatic pageview tracking for Next.js App Router (enabled via
 *   `capture_pageview: true` in POSTHOG_CONFIG)
 * - Environment property registration
 * - Development-only logging for debugging
 *
 * @see {@link https://posthog.com/docs/integrate/client/nextjs PostHog Next.js Documentation}
 * @see {@link docs/POSTHOG_SETUP.md PostHog Setup Guide}
 *
 * @example
 * ```tsx
 * <PostHogProvider>
 *   <App />
 * </PostHogProvider>
 * ```
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  // Don't initialize if keys are missing
  if (!posthogKey || !posthogHost) {
    devLog('warn', 'PostHog not configured', {
      missingKey: !posthogKey,
      missingHost: !posthogHost,
    });
    return <>{children}</>;
  }

  return (
    <PHProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
        ...POSTHOG_CONFIG,
        // Register environment property when PostHog loads
        loaded: (ph) => {
          devLog('debug', 'PostHog initialized successfully', { hasInstance: !!ph });
          // Register environment property for all events
          ph.register({
            environment: process.env.NODE_ENV || 'development',
          });
        },
      }}
    >
      {children}
    </PHProvider>
  );
}
